import { coverageTier } from "./coverage";
import { requirementStatus } from "./requirements";
import type { BillingRequirement, TestEvidence } from "./types";

/**
 * What the Hub wants a QA reader to act on, ranked.
 *
 * The landing screen used to open on one journey's checkpoint detail, which
 * answered a narrow question well and the broad one — "is the platform working
 * well?" — not at all. This ranks the whole picture so the answer is visible
 * before anyone opens a detail screen.
 *
 * `broken` is something automation says is wrong right now. `unproven` is an
 * absence of evidence. They are never merged: a code nothing tests is not a
 * failing code, and treating them alike is what made the old board unreadable.
 */
export type AttentionSeverity = "broken" | "ageing" | "unproven";

export interface AttentionItem {
  id: string;
  severity: AttentionSeverity;
  headline: string;
  detail: string;
  target: "codes" | "tests" | "assurance";
}

const severityRank: AttentionSeverity[] = ["broken", "ageing", "unproven"];

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function attentionItems(
  requirements: BillingRequirement[],
  browserTests: TestEvidence[],
  exactSha?: string,
): AttentionItem[] {
  const items: AttentionItem[] = [];

  const brokenCodes = requirements.filter((item) =>
    ["failed", "flaky"].includes(requirementStatus(item)),
  );
  if (brokenCodes.length > 0) {
    items.push({
      id: "codes-broken",
      severity: "broken",
      headline: `${plural(brokenCodes.length, "charge code is", "charge codes are")} failing`,
      detail: brokenCodes.map((item) => item.code).join(", "),
      target: "codes",
    });
  }

  const brokenTests = browserTests.filter((test) =>
    ["failed", "flaky"].includes(test.status),
  );
  if (brokenTests.length > 0) {
    items.push({
      id: "tests-broken",
      severity: "broken",
      headline: `${plural(brokenTests.length, "browser test is", "browser tests are")} failing or flaky`,
      detail: "A red or intermittent test on this build means a real user path is at risk.",
      target: "tests",
    });
  }

  const ageingCodes = requirements.filter((item) => requirementStatus(item) === "stale");
  if (ageingCodes.length > 0) {
    items.push({
      id: "codes-stale",
      severity: "ageing",
      headline: `${plural(ageingCodes.length, "charge code was", "charge codes were")} last proven on an older build`,
      detail: "Their evidence is real but does not describe the current build.",
      target: "codes",
    });
  }

  const untestedCodes = requirements.filter((item) => requirementStatus(item) === "unlinked");
  if (untestedCodes.length > 0) {
    items.push({
      id: "codes-unlinked",
      severity: "unproven",
      headline: `${plural(untestedCodes.length, "charge code has", "charge codes have")} no automated test`,
      detail: "Reviewed by a person, never demonstrated by the software.",
      target: "codes",
    });
  }

  const neverRun = browserTests.filter((test) => coverageTier(test, exactSha) === "never");
  if (neverRun.length > 0) {
    const specs = new Set(neverRun.map((test) => test.path)).size;
    items.push({
      id: "tests-never-run",
      severity: "unproven",
      headline: `${plural(neverRun.length, "browser test", "browser tests")} are never run by CI`,
      detail: `Across ${plural(specs, "spec file", "spec files")} that sit outside every CI selection.`,
      target: "tests",
    });
  }

  return items.sort(
    (left, right) =>
      severityRank.indexOf(left.severity) - severityRank.indexOf(right.severity),
  );
}

/** True when nothing automation can currently see is wrong. */
export function isHealthy(items: AttentionItem[]): boolean {
  return !items.some((item) => item.severity === "broken");
}
